import React, {useEffect, useState, useRef, useCallback} from 'react'
import {
  Button,
  ParagraphText,
  Modal,
  Icon,
  RadioGroup,
  Select,
} from '@thryvlabs/maverick'
import dayjs from 'dayjs'
import Calendar from 'react-calendar'
import {toast} from 'react-toastify'
import {useAuthClient} from '../utils/use-auth-client'
import {useAsync} from '../utils/use-async'
import Skeleton, {SkeletonTheme} from 'react-loading-skeleton'
import {ModalContent, SelectDateTimeContainer} from './ui/new-demo'
import {UpdateBusinessInfoModal} from './update-business-info-modal'

const REGION_OPTIONS = [
  {name: 'Coastal', value: 'Coastal'},
  {name: 'Midwest', value: 'Midwest'},
  {name: 'South', value: 'South'},
  {name: 'Inside', value: 'Inside'},
  {name: 'Inbound', value: 'Inbound'},
  {name: 'Partner Channel', value: 'Partner Channel'},
]

// FULSAS-12295: geographic region for the Thryv Starter onboarding kickoff
// call — distinct from the sales-team REGION_OPTIONS above. The backend
// resolves the Service ID for the chosen region.
const ONBOARDING_REGION_OPTIONS = [
  {name: 'Coastal', value: 'Coastal'},
  {name: 'Midwest', value: 'Midwest'},
  {name: 'South', value: 'South'},
  {name: 'Inside', value: 'Inside'},
  {name: 'Inbound', value: 'Inbound'},
]

function DateTimeModal({
  modalTitle,
  buttonText,
  modalId,
  saveDateTime,
  defaultDate,
  availableTimeOptions = {},
  staffId,
  selectedTimeStamp,
  address,
  isAlignmentCall = false,
  alignmentCallTimesAvailable,
  isCreativeCall = false,
  isThryvSuccessServicesCall = false,
  customURL = null,
  disabled,
  isFollowup = false,
  radioLanguageOptions,
  setLanguagePreference,
  country,
  isKickoff = false,
  isThryvStarterOnboarding = false,
  isV5,
  isThryvLeadsOrSeoIncluded = false,
  isRestrictedAccessHIPPASelected = false,
  noneCustomVideoCreativeItem = true,
  isUsSpanishOptionAvailable = false,
  customServiceId,
  callType,
  is5Order,
  onOpen,
  updateAddress,
  setAddressNeedsToBeUpdated,
  minDate: minDateProp = new Date(),
  maxDate: maxDateProps,
  disableWeekends = false,
}) {
  const toSafeDate = (val, fallback) => {
    const d = val instanceof Date ? val : new Date(val)
    return isNaN(d.getTime()) ? fallback : d
  }
  const minDate = toSafeDate(minDateProp, new Date())
  const maxDate = toSafeDate(maxDateProps, new Date(Date.now() + 30 * 86400000))
  const client = useAuthClient()
  const {run} = useAsync({
    status: 'pending',
  })
  const [initialLoad, setInitialLoad] = useState(true)
  const [isLoading, setIsLoading] = useState(false)
  const [showError, setShowError] = useState(false)
  const [hours, setHours] = useState([])
  const [selectedHour, setSelectedHour] = useState()
  const [selectedHourTimeStamp, setSelectedHourTimeStamp] = useState()
  const [selectedStaffId, setSelectedStaffId] = useState()
  const [selectedServiceId, setSelectedServiceId] = useState()
  const [selectedDateTime, setSelectedDateTime] = useState(null)
  const [selectedStartDateTime, setSelectedStartDateTime] = useState(null)
  const [timeZoneName, setTimeZoneName] = useState(null)
  const [timeZoneId, setTimeZoneId] = useState(null)
  const [selectedRegion, setSelectedRegion] = useState({
    name: '',
    value: '',
  })
  const [selectedOnboardingRegion, setSelectedOnboardingRegion] = useState({
    name: '',
    value: '',
  })
  const [calendarValue, setCalendarValue] = useState(minDate)
  const [isBusinessInfoUpdated, setIsBusinessInfoUpdated] = useState(false)
  const [updatedFields, setUpdatedFields] = useState({})
  const [openCalendar, setOpenCalendar] = useState(
    isRestrictedAccessHIPPASelected ||
      isThryvLeadsOrSeoIncluded ||
      country !== 'US' ||
      !isKickoff ||
      !is5Order,
  )

  const [selectedDay, setSelectedDay] = useState(minDate)
  const [hasOpened, setHasOpened] = useState(false)

  useEffect(() => {
    if (selectedRegion.value !== '' || selectedOnboardingRegion.value !== '') {
      setOpenCalendar(true)
      setHours([])
      setInitialLoad(true)
      setIsLoading(false)

      if (abortControllerRef.current) {
        abortControllerRef.current.abort('A new request has been initiated.')
      }
    }
  }, [selectedRegion, selectedOnboardingRegion])

  const [selectedLanguagePreference, setSelectedLanguagePreference] = useState({
    name: 'No',
    value: false,
  })
  const abortControllerRef = useRef(null)
  const [continueWithoutAlignmentTime, setContinueWithoutAlignmentTime] =
    useState(false)
  const isOnboarding = isCreativeCall || isKickoff || isThryvSuccessServicesCall
  const displayRegionSelect =
    !isRestrictedAccessHIPPASelected &&
    !isThryvLeadsOrSeoIncluded &&
    isKickoff &&
    country === 'US' &&
    selectedLanguagePreference?.value !== true &&
    is5Order &&
    !isThryvStarterOnboarding
  const displayOnboardingRegionSelect = isThryvStarterOnboarding && isKickoff

  const showOrderCallLanguagePrefs =
    country === 'US' &&
    ((isKickoff &&
      isUsSpanishOptionAvailable &&
      isOnboarding &&
      !isThryvLeadsOrSeoIncluded &&
      (isV5 || hasOpened)) ||
      (isV5 &&
        isCreativeCall &&
        noneCustomVideoCreativeItem &&
        !isRestrictedAccessHIPPASelected))

  const showDemoCallLanguagePrefs =
    country === 'US' && (isAlignmentCall || callType === 'demo')

  const scheduledDemo =
    (isAlignmentCall || callType === 'demo') && selectedDateTime

  const onSave = () => {
    const formattedDay = dayjs(selectedDay).format('MMM-DD-YYYY')
    if (selectedHour && formattedDay) {
      const longDate = `${formattedDay} ${selectedHour}`
      const ISODate = dayjs(selectedStartDateTime).toISOString()
      setSelectedDateTime(longDate)
      setShowError(false)
      setLanguagePreference(selectedLanguagePreference)
      saveDateTime({
        longDate,
        selectedStaffId,
        selectedHourTimeStamp,
        ISODate,
        timeZoneId,
        timeZoneName,
        selectedServiceId,
        startDateTime: selectedStartDateTime,
        alignmentCallTimesAvailable: true,
        originalApptId: availableTimeOptions?.originalApptId,
        callType,
        selectedLanguagePreference,
        updatedFields,
      })
    } else if (continueWithoutAlignmentTime && isAlignmentCall) {
      alignmentCallTimesAvailable = false
      saveDateTime({alignmentCallTimesAvailable, callType})
    } else {
      setShowError(true)
      return
    }
  }

  const onSelectHour = ({
    staff_id,
    local_start_time,
    timeStamp,
    service_id,
    startDateTime,
  }) => {
    setSelectedStaffId(staff_id)
    setSelectedHour(local_start_time)
    setSelectedHourTimeStamp(timeStamp)
    setSelectedServiceId(service_id)
    setSelectedStartDateTime(startDateTime)
  }

  const fetchAvailableHours = useCallback(
    async selectedCalendarDay => {
      setIsLoading(true)
      const formattedDate = dayjs(selectedCalendarDay || selectedDay).format(
        'YYYY-MM-DD',
      )
      let url = ''
      if (customURL) {
        url = `${customURL}&date=${formattedDate}`
      } else if (isFollowup && Object.keys(updatedFields).length === 0) {
        url =
          `demos/available-times?date=${formattedDate}&isFollowup=${true}` +
          `&staff_id=${availableTimeOptions.staffId}` +
          `&country=${availableTimeOptions.prospectsCountry}` +
          `&address=${encodeURIComponent(
            `${availableTimeOptions.streetAddress} ${availableTimeOptions.prospectsCity} ${availableTimeOptions.prospectsState}`,
          )}`
      } else if (isFollowup && updatedFields) {
        url =
          `demos/available-times?date=${formattedDate}&isFollowup=${true}` +
          `&staff_id=${availableTimeOptions.staffId}` +
          `&country=${updatedFields.prospectsCountry.value}` +
          `&address=${encodeURIComponent(
            `${updatedFields.streetAddress} ${updatedFields.prospectsCity} ${updatedFields.prospectsState.value}`,
          )}`
      } else {
        url =
          `demos/available-times?date=${formattedDate}` +
          `&country=${availableTimeOptions.prospectsCountry}` +
          `&isMarketingPlatform=${availableTimeOptions.isMarketingPlatform}` +
          `&isSpanish=${availableTimeOptions.demoConductedInSpanish}` +
          `&address=${encodeURIComponent(address)}` +
          `&isFormerVivialCustomer=${availableTimeOptions.isFormerVivialCustomer}` +
          `&isKeapDemo=${availableTimeOptions.isKeapDemo}` +
          `&isWorkforceCenterDemo=${availableTimeOptions.isWorkforceCenterDemo}`
      }
      if (availableTimeOptions.regionId) {
        url = `${url}&region=${availableTimeOptions.regionId}`
      }
      if (!isAlignmentCall) {
        if (availableTimeOptions.isMarketingCenter) {
          url = `${url}&isMarketingCenter=${availableTimeOptions.isMarketingCenter}`
        }
      } else if (isAlignmentCall) {
        url =
          `${url}&staff_id=${staffId}` +
          `&isAlignment=${isAlignmentCall}` +
          `&isMarketingCenter=${availableTimeOptions.isMarketingCenter}`
      }

      if (customServiceId) {
        url += `&serviceId=${customServiceId}`
      }

      if (selectedRegion.value !== '') {
        url += `&region=${selectedRegion.value}`
      }

      if (selectedOnboardingRegion.value !== '') {
        url += `&region=${selectedOnboardingRegion.value}`
      }

      if (abortControllerRef.current) {
        abortControllerRef.current.abort('A new request has been initiated.')
      }

      abortControllerRef.current = new AbortController()
      const abortSignal = abortControllerRef.current.signal

      try {
        const {
          warning,
          availableTimes,
          availableDemoTimes,
          timeZone,
          serviceIdTitle,
          serviceId,
        } = await run(client(url, {signal: abortSignal}))

        if (warning) {
          const {message} = warning[0]
          toast.warning(message)
        }

        let _availableDemoTimes = []
        if (customURL) {
          setTimeZoneName(timeZone.timeZoneName)
          setTimeZoneId(timeZone.timeZoneId)
          _availableDemoTimes = availableTimes.map(hour => {
            const availableTime = Date.parse(hour.startTime)
            return {
              ...hour,
              staff_id: hour.staffId,
              local_start_time: dayjs(hour.startTime)
                .utc()
                .tz(timeZone.timeZoneId)
                .format('hh:mm A'),
              timeStamp: availableTime / 1000,
              service_id: serviceIdTitle.serviceId,
            }
          })
        } else {
          setTimeZoneName(timeZone.timeZoneName)
          setTimeZoneId(timeZone.timeZoneId)
          _availableDemoTimes = availableDemoTimes.map(hour => {
            const availableTime = Date.parse(hour.start_time)
            return {
              ...hour,
              local_start_time: dayjs(hour.start_time)
                .utc()
                .tz(timeZone.timeZoneId)
                .format('hh:mm A'),
              timeStamp: availableTime / 1000,
              service_id: serviceId,
            }
          })
        }

        if (selectedTimeStamp) {
          _availableDemoTimes = _availableDemoTimes.filter(
            ({timeStamp}) => selectedTimeStamp > timeStamp,
          )
        }

        if (_availableDemoTimes.length === 0) {
          setContinueWithoutAlignmentTime(true)
        } else {
          setContinueWithoutAlignmentTime(false)
        }

        setHours(_availableDemoTimes)
        setIsLoading(false)
        abortControllerRef.current = null
      } catch (err) {
        if (abortSignal.aborted === false) {
          toast.error(err.message)
          setIsLoading(false)
        }
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [selectedDay, availableTimeOptions, staffId, address, updatedFields],
  )

  useEffect(() => {
    if (
      country === 'US' &&
      selectedLanguagePreference?.value === true &&
      is5Order
    ) {
      setOpenCalendar(true)
    } else if (
      country === 'US' &&
      displayRegionSelect === true &&
      selectedLanguagePreference?.value === false &&
      selectedRegion?.value === '' &&
      is5Order
    ) {
      setOpenCalendar(false)
    }
  }, [
    country,
    displayRegionSelect,
    is5Order,
    selectedLanguagePreference?.value,
    selectedRegion?.value,
  ])

  useEffect(() => {
    if (isFollowup && updateAddress && !isBusinessInfoUpdated) {
      setOpenCalendar(false)
    }
    if (
      isFollowup &&
      (!updateAddress || (updateAddress && isBusinessInfoUpdated))
    ) {
      setOpenCalendar(true)
    }
  }, [isFollowup, updateAddress, isBusinessInfoUpdated])

  const onSelectedDay = selectedCalendarDay => {
    if (initialLoad) {
      setInitialLoad(false)
    }
    setSelectedDay(selectedCalendarDay)
    fetchAvailableHours(selectedCalendarDay)
  }

  const getButtonText = () => {
    if (isFollowup) {
      return 'Schedule Follow-up'
    } else {
      return isAlignmentCall && continueWithoutAlignmentTime
        ? 'Continue without Alignment Call'
        : 'Save'
    }
  }

  const onLanguagePreferenceChange = e => {
    setSelectedLanguagePreference(e)
    setLanguagePreference(e)
    setSelectedDay(new Date() || defaultDate)
    setHours([])
    setSelectedStaffId()
    setSelectedHour()
    setSelectedHourTimeStamp()
    setSelectedServiceId()
    setSelectedStartDateTime()
  }

  const handleContinue = data => {
    const updatedBusinessInfo = {
      streetAddress: data.streetAddress,
      prospectsCity: data.prospectsCity,
      prospectsState: data.prospectsState,
      prospectsCountry: data.prospectsCountry,
    }
    setUpdatedFields(updatedBusinessInfo)
  }

  useEffect(() => {
    const minTime = minDate.getTime()
    const maxTime = maxDate.getTime()
    const selectedTime = selectedDay.getTime()
    if (minTime > selectedTime) {
      setSelectedDay(minDate)
      setCalendarValue(minDate)
    } else if (maxTime < selectedTime) {
      setSelectedDay(maxDate)
      setCalendarValue(minDate)
    }
  }, [minDate, maxDate, selectedDay])

  const modalProps = {
    variant: 'lg',
    title: modalTitle,
    footerCancel: true,
    btnActionText: getButtonText(),
    actionClose: true,
    footer: true,
    action: true,
    onOpen: () => {
      setHasOpened(true)
      onOpen?.()
    },
    onClose: () => {
      setTimeout(() => {
        setHours([])
        setInitialLoad(true)
      }, 300)
      if (isFollowup) {
        setAddressNeedsToBeUpdated(false)
      }
    },
    id: modalId,
    disableModal: disabled,
    btnAction: onSave,
  }
  if (callType === 'demo' || callType === 'alignment') {
    modalProps.altBtn = (
      <Button
        variant={
          scheduledDemo || buttonText === 'No Alignment Times Available'
            ? 'text'
            : 'primary'
        }
        buttonType={
          scheduledDemo || buttonText === 'No Alignment Times Available'
            ? ''
            : 'outlined'
        }
        level={
          scheduledDemo || buttonText === 'No Alignment Times Available'
            ? 1
            : ''
        }
      >
        {buttonText}
      </Button>
    )
  }
  if (callType !== 'demo' && !isAlignmentCall) {
    modalProps.btnText = buttonText
    modalProps.btnType = isFollowup ? 'secondary' : 'primary'
  }
  if (isAlignmentCall) {
    modalProps.btnText = buttonText
    modalProps.btnType = 'primary'
  }
  if (updateAddress) {
    modalProps.btnActionDisabled = !isBusinessInfoUpdated || !selectedHour
  } else if (isFollowup) {
    modalProps.btnActionDisabled = !selectedHour
  } else if (isOnboarding) {
    modalProps.btnActionDisabled = !selectedHour
  }

  return (
    <div className="d-flex align-items-center dateTimeContainer">
      <Modal {...modalProps}>
        <ModalContent>
          <div style={{gridRowStart: 1, justifySelf: 'start'}} className="pb-2">
            {(showOrderCallLanguagePrefs || showDemoCallLanguagePrefs) && (
              <RadioGroup
                data={radioLanguageOptions}
                value={selectedLanguagePreference}
                label="Would you like to schedule this call in Spanish?"
                row
                layoutStyles={{gap: '10px'}}
                onChange={onLanguagePreferenceChange}
                disabled={isLoading}
              />
            )}
            {displayRegionSelect && (
              <div className="mb-5" style={{marginTop: '30px'}}>
                <Select
                  selectLabel="Select Region"
                  options={REGION_OPTIONS}
                  width="md"
                  selectedOption={selectedRegion}
                  setSelectedOption={setSelectedRegion}
                />
              </div>
            )}
            {displayOnboardingRegionSelect && (
              <div className="mb-5" style={{marginTop: '30px'}}>
                <Select
                  selectLabel="Select Region"
                  options={ONBOARDING_REGION_OPTIONS}
                  width="md"
                  selectedOption={selectedOnboardingRegion}
                  setSelectedOption={setSelectedOnboardingRegion}
                />
              </div>
            )}
          </div>
          {updateAddress && !isBusinessInfoUpdated && (
            <>
              <ParagraphText variant="lg">
                No business address information found. Please, update the
                address to continue.
              </ParagraphText>
              <UpdateBusinessInfoModal
                nextStep={handleContinue}
                setIsBusinessInfoUpdated={setIsBusinessInfoUpdated}
                isBusinessInfoUpdated={isBusinessInfoUpdated}
              />
            </>
          )}
          {openCalendar && (
            <SelectDateTimeContainer style={{gridRowStart: 2}}>
              {showError && (
                <ParagraphText variant="lg">
                  Please, select a date
                </ParagraphText>
              )}
              <div className="mt-4">
                <ParagraphText variant="lg">Select a date</ParagraphText>
                <div className="calendar" style={{height: '275px'}}>
                  <Calendar
                    minDate={minDate}
                    maxDate={maxDate}
                    value={calendarValue}
                    onChange={setCalendarValue}
                    calendarType="US"
                    next2Label={null}
                    prev2Label={null}
                    nextLabel={
                      <Icon
                        type="solid"
                        variant="arrowRight"
                        className="m-auto"
                      />
                    }
                    prevLabel={
                      <Icon
                        type="solid"
                        variant="arrowLeft"
                        className="m-auto"
                      />
                    }
                    tileClassName="calendar-tile"
                    onClickDay={onSelectedDay}
                    tileDisabled={({date, view}) => {
                      if (view === 'month' && disableWeekends) {
                        const day = date.getDay()
                        return day === 0 || day === 6 // Sunday (0) or Saturday (6)
                      }
                      return false
                    }}
                  />
                </div>
              </div>
              <div className="ml-4">
                <ParagraphText variant="lg" className="mb-0 mt-4">
                  Select a time
                </ParagraphText>
                {timeZoneName && (
                  <ParagraphText variant="lg">
                    <b>({timeZoneName})</b>
                  </ParagraphText>
                )}
                <div className={`hours-container ${isLoading && 'isLoading'}`}>
                  {isLoading ? (
                    <>
                      <SkeletonTheme color="#e6e6e6" highlightColor="#fff">
                        <Skeleton
                          containerTestId="new-demo-request-hours-loader"
                          height="35px"
                          width="100%"
                        />
                        <Skeleton className="mt-2" height="35px" width="100%" />
                        <Skeleton className="mt-2" height="35px" width="100%" />
                        <Skeleton className="mt-2" height="35px" width="100%" />
                        <Skeleton className="mt-2" height="35px" width="100%" />
                        <Skeleton className="mt-2" height="35px" width="100%" />
                        <Skeleton className="mt-2" height="35px" width="100%" />
                      </SkeletonTheme>
                    </>
                  ) : (
                    <>
                      {hours.length === 0 ? (
                        initialLoad ? (
                          <>
                            <p className="text-btn-links font-normal font-montserrat text-thryv-black-500">
                              Please select a date to view available
                              appointments.
                            </p>
                          </>
                        ) : (
                          <>
                            {isAlignmentCall ? (
                              <p className="text-btn-links font-bold font-montserrat text-thryv-black-500">
                                There are no times available for an alignment
                                call on this date. If an alignment call cannot
                                be scheduled, please reach out to your SST to
                                speak about the account prior to the scheduled
                                demo.
                              </p>
                            ) : (
                              <p className="text-btn-links font-normal font-montserrat text-thryv-black-500">
                                Sorry, we could not find any results for the
                                given date. Try selecting a different date.
                              </p>
                            )}
                          </>
                        )
                      ) : (
                        <>
                          {hours.map(
                            (
                              {
                                local_start_time,
                                staff_id,
                                timeStamp,
                                service_id,
                                startDateTime,
                              },
                              i,
                            ) => (
                              <Button
                                key={i}
                                variant="secondary"
                                className={`hour-option ${
                                  selectedHour === local_start_time
                                    ? 'selected'
                                    : ''
                                }`}
                                onClick={() =>
                                  onSelectHour({
                                    staff_id,
                                    local_start_time,
                                    timeStamp,
                                    service_id,
                                    startDateTime,
                                  })
                                }
                              >
                                {local_start_time}
                              </Button>
                            ),
                          )}
                        </>
                      )}
                    </>
                  )}
                </div>
              </div>
            </SelectDateTimeContainer>
          )}
        </ModalContent>
      </Modal>
      {selectedDateTime && (
        <ParagraphText
          variant="reg"
          className="ml-2 mb-0"
          data-testid="select-date-time"
        >
          {selectedDateTime}
        </ParagraphText>
      )}
    </div>
  )
}

export {DateTimeModal}
